import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ItemState } from '../../../models'
import { Typography, useMediaQuery, useTheme } from '@material-ui/core'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import { Loader } from '../../../components/loader'
import useEmblaCarousel from 'embla-carousel-react'
import { useNonprofitItemsComponentStyles } from './nonprofit-items-component.styles'
import { homePageItems } from '../../../services/api/home-page-items'
import { NonprofitItemsComponentProps } from './nonprofit-items-component.types'
import { ListedItem, ListedItemProps } from '../../../components/listed-item'

export const NonprofitItems: React.FC<NonprofitItemsComponentProps> = (
  props: NonprofitItemsComponentProps
) => {
  const { nonprofitId, categoryId, setNonprofitItems } = props

  const [items, setItems] = useState<any>([])
  const theme = useTheme()
  const [loading, setLoading] = useState<boolean>(true)
  const classes = useNonprofitItemsComponentStyles()
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)
  const { t } = useTranslation()

  useEffect(() => {
    const requestParams: any = {
      priceMin: 0,
      priceMax: 200000,
      pageSize: 12,
      pageIndex: 0,
      showSold: false,
      nonprofitId: nonprofitId,
    }

    if (nonprofitId !== undefined) {
      homePageItems
        .availableListedItems(requestParams)
        .then((data) => setItems(data.values))
      setLoading(false)
    }
  }, [nonprofitId])

  useEffect(() => {
    if (!items) setNonprofitItems(false)
  }, [items])

  const nonprofitItemsSearchLink = `/search?pageIndex=1&pageSize=12&nonprofitCategoryId=${categoryId}&nonprofitId=${nonprofitId}`

  // Determine screen size - check from largest to smallest
  const isXl = useMediaQuery(theme.breakpoints.up('xl')) // ≥1920px
  const isLg = useMediaQuery(theme.breakpoints.between('lg', 'xl')) // 1280px-1919px
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg')) // 960px-1279px
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md')) // 600px-959px
  const isXs = useMediaQuery(theme.breakpoints.down('sm')) // <600px

  // Get number of slides to scroll based on screen size
  // Should match the number of visible slides for better UX
  const getSlidesToScroll = useCallback(() => {
    if (isXl) return 1 // Extra large screens (≥1920px): 4 visible, scroll by 1
    if (isLg) return 1 // Large screens (1280px-1919px): 4 visible, scroll by 1
    if (isMd) return 1 // Medium screens (960px-1279px): 3 visible, scroll by 1
    if (isSm) return 1 // Small screens (600px-959px): 2 visible, scroll by 1
    if (isXs) return 1 // Extra small screens (<600px): 1 visible, scroll by 1
    return 1 // Default: scroll by 1
  }, [isXs, isSm, isMd, isLg, isXl])

  // Base options for embla-carousel
  const OPTIONS = {
    align: 'start' as const,
    loop: false,
    skipSnaps: false,
    dragFree: true,
    containScroll: 'trimSnaps' as const,
  }

  const [emblaRef, emblaApi] = useEmblaCarousel(OPTIONS)
  const [slidesToScroll, setSlidesToScroll] = useState(getSlidesToScroll())

  // Update slidesToScroll when screen size changes and reinitialize carousel
  useEffect(() => {
    const newSlidesToScroll = getSlidesToScroll()
    if (newSlidesToScroll !== slidesToScroll) {
      setSlidesToScroll(newSlidesToScroll)
    }
  }, [isXs, isSm, isMd, isLg, isXl, getSlidesToScroll, slidesToScroll])

  // Reinitialize carousel when screen size changes
  useEffect(() => {
    if (emblaApi) {
      emblaApi.reInit()
    }
  }, [emblaApi, slidesToScroll])

  // Custom scroll functions that scroll by multiple slides
  const scrollPrev = useCallback(() => {
    if (!emblaApi) return

    const currentIndex = emblaApi.selectedScrollSnap()
    const newIndex = Math.max(0, currentIndex - slidesToScroll)
    emblaApi.scrollTo(newIndex)
  }, [emblaApi, slidesToScroll])

  const scrollNext = useCallback(() => {
    if (!emblaApi) return

    const currentIndex = emblaApi.selectedScrollSnap()
    const scrollSnaps = emblaApi.scrollSnapList()
    const newIndex = Math.min(
      scrollSnaps.length - 1,
      currentIndex + slidesToScroll
    )
    emblaApi.scrollTo(newIndex)
  }, [emblaApi, slidesToScroll])

  const onSelect = useCallback((emblaApi: any) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect)
    emblaApi.on('select', onSelect)
  }, [emblaApi, onSelect])

  return (
    <div className={classes.section}>
      <div className={classes.sliderHolder}>
        {loading ? (
          <Loader />
        ) : (
          <>
            {items?.length ? (
              <div className={classes.header}>
                <Typography variant="h6" className={[classes.bold].join(' ')}>
                  {t('nonprofitCatalog.itemsFromThisNonprofit')}
                </Typography>
                <a
                  href={nonprofitItemsSearchLink}
                  className={[
                    classes.bold,
                    classes.nonprofitItemsSearchLink,
                  ].join(' ')}
                >
                  {t('nonprofitCatalog.seeAll')}
                </a>
              </div>
            ) : (
              <></>
            )}
            <div className={classes.embla} ref={emblaRef}>
              <div className={classes.emblaContainer}>
                {items?.length ? (
                  [...items].map((item: ListedItemProps, key: number) => (
                    <div className={classes.emblaSlide} key={key}>
                      <ListedItem
                        {...item}
                        state={
                          item.state === ItemState.Active
                            ? undefined
                            : item.state
                        }
                      />
                    </div>
                  ))
                ) : (
                  <></>
                )}
              </div>
            </div>
            {items?.length ? (
              <>
                <button
                  className={`${classes.emblaButton} ${classes.emblaButtonPrev}`}
                  onClick={scrollPrev}
                  disabled={prevBtnDisabled}
                >
                  <ChevronLeftIcon />
                </button>
                <button
                  className={`${classes.emblaButton} ${classes.emblaButtonNext}`}
                  onClick={scrollNext}
                  disabled={nextBtnDisabled}
                >
                  <ChevronRightIcon />
                </button>
              </>
            ) : (
              <></>
            )}
          </>
        )}
      </div>
    </div>
  )
}
